import React from 'react';
import {
  Typography,
  Box,
  Link,
  LinkProps,
  styled,
  useTheme,
} from '@material-ui/core';

import LimbotRainyDay from '@/assets/LimbotRainyDay.svg';

const ResourceLink = ({ children, ...props }: LinkProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ color: theme.palette.primary.light, display: 'inline-block' }}>
      <Link
        target="_blank"
        rel="noopener noreferrer"
        color="inherit"
        style={{
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          fontWeight: 700,
        }}
        {...props}
      >
        <span>{children}</span>
      </Link>
    </Box>
  );
};

const ContentContainer = styled(Box)({
  marginTop: 8,
  '& > *': {
    marginBottom: 8,
  },
});

type CoverageRejectionProps = {
  isCaregiver: boolean;
};

export const CoverageRejection: React.FC<CoverageRejectionProps> = ({ isCaregiver }) => {
  const caregiverText = 'Your dependent is not eligible for Spark Direct through your insurance or employer.';
  const patientText = 'You are not eligible for Spark Direct through your insurance or employer.';
  return (
    <>
      <Typography variant="h2">
        We’re sorry!
      </Typography>
      <Typography>
        {isCaregiver ? caregiverText : patientText}
      </Typography>
      <Typography>
        {
          `If you have any questions or need technical support, 
          please contact us at spark@bighealth.com`
        }
      </Typography>
      <Box
        sx={{
          width: '100%', textAlign: 'center', padding: '32px 0',
        }}
      >
        <img src={LimbotRainyDay} alt="rainy day limbot" style={{ maxWidth: '240px' }} />
      </Box>
      <Typography variant="h2">
        Experiencing a mental health emergency?
      </Typography>
      <Typography style={{ marginBottom: 8 }}>
        Spark Direct is not an emergency service provider.
        {' '}
        If you are currently experiencing a mental health emergency or
        {' '}
        having thoughts of self harm and suicide, please call 911 or go to the nearest emergency room.
      </Typography>
      <Typography style={{ marginBottom: 8 }}>
        You can also
        {' '}
        <span style={{ fontWeight: 700 }}>call or text 988,</span>
        {' '}
        a 24/7 suicide and crisis lifeline. They will connect
        {' '}
        you to a
        {' '}
        <span style={{ fontWeight: 700 }}>trained crisis worker</span>
        {' '}
        who can help you find support right away.
      </Typography>
      <Typography style={{ marginBottom: 8 }}>
        If you are not in immediate danger but would like additional help, you can call
        {' '}
        or text a trained crisis worker who can give you immediate support. The services
        {' '}
        below are free and available 24/7, unless noted otherwise:
      </Typography>
      <ContentContainer>
        <Typography variant="subtitle2">
          988 Suicide and Crisis Lifeline (includes self-harm):
        </Typography>
        <Typography>
          Call
          <ResourceLink href="tel:988"> 988</ResourceLink>
          <br />
          Chat online at:
          <br />
          <ResourceLink href="https://988lifeline.org/chat/" underline="always">
            https://988lifeline.org/chat/
          </ResourceLink>
          .
        </Typography>
        <Typography variant="subtitle2">Crisis Text Line:</Typography>
        <Typography>
          Text
          {' '}
          <b>HOME</b>
          {' '}
          to
          {' '}
          <ResourceLink href="tel:741741">741741</ResourceLink>
        </Typography>
        <Typography variant="subtitle2">
          Disaster Distress Helpline (natural disasters, terrorism, COVID-19)
        </Typography>
        <Typography>
          Call
          {' '}
          <ResourceLink href="tel:1-800-985-5990">
            1-800-985-5990
          </ResourceLink>
        </Typography>
        <Typography variant="h3">
          Resources for trans people of all ages
        </Typography>
        <Typography variant="subtitle2">
          Trans Lifeline
        </Typography>
        <Typography>
          Not available 24/7. If you are not able to get through, consider
          trying a different service listed on this page
          Call
          {' '}
          <ResourceLink href="tel:1-877-565-8860">1-877-565-8860</ResourceLink>
        </Typography>
        <Typography variant="h3">
          Resources for LGBTQ teens and youth
        </Typography>
        <Typography variant="subtitle2">The Trevor Project</Typography>
        <Typography>
          Call
          {' '}
          <ResourceLink href="tel:1-866-488-7386">1-866-488-7386</ResourceLink>
          <br />
          Text
          {' '}
          <b>START</b>
          {' '}
          to
          {' '}
          <ResourceLink href="tel:678-678">678-678</ResourceLink>
          <br />
          Chat online at:
          <br />
          <ResourceLink href="www.thetrevorproject.org/get-help/" underline="always">
            www.thetrevorproject.org/get-help/
          </ResourceLink>
          .
        </Typography>
        <Typography variant="h3">
          For young people of color
        </Typography>
        <Typography variant="subtitle2">
          Crisis Text Line
        </Typography>
        <Typography>
          Text
          {' '}
          <b>STEVE</b>
          {' '}
          to
          {' '}
          <ResourceLink href="tel:741741">
            741741
          </ResourceLink>
        </Typography>
        <Typography variant="h3">
          Additional resources for non urgent needs
        </Typography>
        <Typography>
          Spark Direct was not designed to replace depression treatments, but to help you live well with depression.
        </Typography>
        <Typography>
          {/* eslint-disable-next-line max-len */}
          If you’re looking for treatment options to use alongside Spark Direct, you can find information in the following places:
        </Typography>
        <Typography variant="subtitle2" style={{ marginTop: 8 }}>
          The National Institute of Mental Health:
        </Typography>
        <ResourceLink href="https://www.nimh.nih.gov/health/publications/depression-listing" underline="always">
          https://www.nimh.nih.gov/health/publications/depression-listing
        </ResourceLink>
        <Typography variant="subtitle2" style={{ marginTop: 8 }}>
          The American Psychological Association:
        </Typography>
        <ResourceLink href="www.apa.org/topics/depression" underline="always">
          www.apa.org/topics/depression
        </ResourceLink>
        <Typography variant="subtitle2" style={{ marginTop: 8 }}>
          The American Academy of Child and Adolescent Psychiatry:
        </Typography>
        <ResourceLink
          // eslint-disable-next-line max-len
          href="www.aacap.org/AACAP/Families_and_Youth/Resource_Centers/Depression_Resource_Center/Depression_Resource_Center.aspx"
          underline="always"
        >
          {/* eslint-disable-next-line max-len */}
          www.aacap.org/AACAP/Families_and_Youth/Resource_Centers/Depression_Resource_Center/Depression_Resource_Center.aspx
        </ResourceLink>
        <Typography variant="subtitle2" style={{ marginTop: 8 }}>
          The National Alliance on Mental Illness:
        </Typography>
        <ResourceLink href="www.nami.org/Your-Journey/Teens-Young-Adults" underline="always">
          www.nami.org/Your-Journey/Teens-Young-Adults
        </ResourceLink>

        <Typography>
          {`The third-party service provider resources provided above are not owned or controlled by Big Health. 
          Big Health is not responsible for any actions or inactions between you and any third-party service provider. 
          By using or visiting any of the third-party service provider resources you acknowledge and agree that 
          Big Health is not responsible for and expressly disclaims all liability for the actions or inactions of any 
          third-party service provider.`}
        </Typography>
      </ContentContainer>
    </>
  );
};
